// Optimized AutocompleteGoogle.tsx with clean UX, keyboard support, and styling

import { AffiliateLandingInterface } from 'types/affiliate';
import Image from 'next/image';
import React, {
  useEffect,
  forwardRef,
  useRef,
  useState,
} from 'react';

declare let window: any;

const loadScript = (url: string, callback: () => void) => {
  if (document.getElementById('google-maps-script')) {
    callback();
    return;
  }
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.id = 'google-maps-script';
  script.src = url;
  script.onload = () => callback();
  document.head.appendChild(script);
};

interface Props {
  onSearch?: () => void;
  setEnableSearch?: (enabled: boolean) => void;
  hasMRA?: boolean;
  affiliateByRef?: AffiliateLandingInterface;
}

export type Ref = HTMLInputElement;

export const AutocompleteGoogle = forwardRef<Ref, Props>((props, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredResults, setFilteredResults] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const sessionToken = useRef<any>(null);
  const autocompleteService = useRef<any>(null);
  const geocoder = useRef<any>(null);
  const dropdownRef = useRef<any>(null);

  const handleSelect = (result: any) => {
    setInputValue(`${result.description} (${result.state})`);
    setFilteredResults([]);
    setIsOpen(false);
    props?.onSearch?.();
  };

  const filterResults = (predictions: any, value: string) => {
    const promises = predictions.map((prediction: any) => {
      return new Promise((resolve) => {
        geocoder.current.geocode({ placeId: prediction.place_id }, (results: any, status: any) => {
          if (status === 'OK' && results[0]) {
            const state = results[0].address_components.find((c: any) =>
              c.types.includes('administrative_area_level_1')
            )?.short_name;

            const stateRestriction = props.affiliateByRef?.state_restriction
              ? JSON.parse(props.affiliateByRef.state_restriction)
              : [];

            const isAllowed =
              props.affiliateByRef?.restriction_type === 'area' ||
              (props.affiliateByRef?.restriction_type === 'state' && stateRestriction.includes(state));

            if (isAllowed) resolve({ description: prediction.description, state });
            else resolve(null);
          } else resolve(null);
        });
      });
    });

    Promise.all(promises).then((results: any) => {
      const valid = results.filter((r: any) => r);
      setFilteredResults(valid);
      setSelectedIndex(0);
      setIsOpen(true);
    });
  };

  const handleInputChange = (e: any) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedIndex(0);
    if (value.length <= 1) return setFilteredResults([]);

    const request: any = {
      input: value,
      sessionToken: sessionToken.current,
      componentRestrictions: { country: 'us' },
    };

    if (props.affiliateByRef?.restriction_type === 'area') {
      Object.assign(request, {
        strictBounds: true,
        locationRestriction: {
          north: +props.affiliateByRef.northeast_lat_restriction,
          south: +props.affiliateByRef.southwest_lat_restriction,
          east: +props.affiliateByRef.northeast_long_restriction,
          west: +props.affiliateByRef.southwest_long_restriction,
        },
        types: ['(regions)'],
      });
    }

    autocompleteService.current.getPlacePredictions(request, (predictions: any, status: any) => {
      if (status === 'OK') filterResults(predictions, value);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex((prev) => Math.min(prev + 1, filteredResults.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && filteredResults[selectedIndex]) {
      handleSelect(filteredResults[selectedIndex]);
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (!window.google && !document.getElementById('google-maps-script')) {
      props.setEnableSearch?.(true);
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}&libraries=places`,
        () => {
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          geocoder.current = new window.google.maps.Geocoder();
        }
      );
    } else if (window.google) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      geocoder.current = new window.google.maps.Geocoder();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full">
      <input
        id="location_search_input"
        ref={ref}
        type="search"
        className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring"
        placeholder={props.hasMRA ? 'City, State' : 'City, state, or zip code'}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        onFocus={() => filteredResults.length > 0 && setIsOpen(true)}
      />
      {isOpen && filteredResults.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md bg-white shadow-lg max-h-60 overflow-y-auto border border-gray-300"
        >
          {filteredResults.map((item, index) => (
            <div
              key={`${item.description}-${index}`}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                selectedIndex === index ? 'bg-blue-100 font-semibold' : ''
              }`}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => handleSelect(item)}
            >
              {item.description} ({item.state})
            </div>
          ))}
          <div className="flex justify-end p-2 border-t">
            <Image src="/powered-by-google-on-white3.png" alt="powered" width={120} height={10} />
          </div>
        </div>
      )}
    </div>
  );
});
